import React,{useEffect,useMemo,useState} from 'react';
import {AppState,Platform} from 'react-native';
import {View,Text,StyleSheet,ScrollView,TouchableOpacity} from '../components/rn';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTheme} from '../theme/ThemeContext';
import {radius,spacing} from '../theme';
import {profilePalette} from '../theme/profileSection';
import {useTranslation} from '../i18n/I18nContext';
import CompactActionButton from '../components/CompactActionButton';
import PageHeader from '../components/PageHeader';
import ProfileSection from '../components/ProfileSection';
import {InfoButton,InfoText,useInfo} from '../components/InfoHint';
import DiscoverMapCanvas from '../components/DiscoverMapCanvas';
import {mapPoints} from '../components/discoverMapGeometry';
import {createDiscoverMapSession,type DiscoverMapState} from '../media/discoverMapSession';
import {discoverMapRequest,discoverMapInterest} from '../services/api';

type Props={token:string;myId:number;onClose:()=>void};
const browserDocument=(globalThis as unknown as {document?:{visibilityState:string;
  addEventListener:(name:string,handler:()=>void)=>void;removeEventListener:(name:string,handler:()=>void)=>void}}).document;
export default function DiscoverMapScreen({token,myId,onClose}:Props){
  const colors=useTheme(),{t,lang}=useTranslation(),insets=useSafeAreaInsets();
  const palette=profilePalette(colors),info=useInfo();
  const [state,setState]=useState<DiscoverMapState>({status:'idle',shares:[]});
  const [foreground,setForeground]=useState(AppState.currentState==='active');
  const [selected,setSelected]=useState<number|null>(null),[fit,setFit]=useState(0),[failed,setFailed]=useState(false);
  const session=useMemo(()=>createDiscoverMapSession({fetchPage:(_id,after,signal)=>discoverMapRequest(token,after,signal),
    interest:(ids,signal)=>discoverMapInterest(token,ids,signal),publish:setState}),[token]);
  useEffect(()=>{const sub=AppState.addEventListener('change',value=>setForeground(value==='active'));
    const browser=()=>setForeground(browserDocument?.visibilityState==='visible');
    if(Platform.OS==='web'){browser();browserDocument?.addEventListener('visibilitychange',browser);}
    return()=>{sub.remove();if(Platform.OS==='web')browserDocument?.removeEventListener('visibilitychange',browser);};
  },[]);
  useEffect(()=>{session.setContext(myId,true,foreground);return()=>session.dispose();},[session,myId,foreground]);
  const points=useMemo(()=>mapPoints(state.shares),[state.shares]);
  const visible=foreground&&state.status!=='error'&&points.length>0;
  return <View testID="map-screen" style={[styles.screen,{backgroundColor:colors.white,paddingBottom:insets.bottom}]}>
    <PageHeader title={t('map.title')} onBack={onClose} backTestID="map-close"
      action={<InfoButton open={info.open} onPress={info.toggle} subject={t('map.title')} testID="map-info"/>}/>
    <InfoText open={info.open} text={t('map.hint')} testID="map-info-text" style={styles.hint}/>
    {visible&&!failed?<View style={styles.canvas}><DiscoverMapCanvas points={points} selected={selected} fit={fit}
      onSelect={setSelected} onFailure={()=>setFailed(true)} label={t('map.title')}/></View>:
      <View style={styles.empty}><Text style={{color:colors.black}}>{t(failed?'map.providerError':state.status==='error'?'map.error':
        state.status==='loading'?'common.loading':state.shares.length?'map.waiting':'map.empty')}</Text></View>}
    <View style={styles.actions}>
      <CompactActionButton label={t('map.fit')} variant="plain" disabled={!points.length} testID="map-fit"
        onPress={()=>{setSelected(null);setFit(n=>n+1);}}/>
      <CompactActionButton label={t('common.retry')} variant="plain" testID="map-retry"
        onPress={()=>{setFailed(false);void session.refresh();}}/>
    </View>
    <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
      {foreground&&state.shares.length>0&&<ProfileSection testID="map-shares" separated>
      {state.shares.map(s=><TouchableOpacity key={s.id} testID={`map-share-${s.id}`} accessibilityRole="button"
        accessibilityState={{selected:selected===s.id,disabled:s.latitude===null}}
        disabled={s.latitude===null} onPress={()=>setSelected(s.id)}
        style={[styles.row,{backgroundColor:selected===s.id?palette.selectedSurface:colors.white}]}>
        <Text style={[styles.name,{color:selected===s.id?palette.selectedText:colors.black}]}>{s.name??t('map.unknown')}</Text>
        <Text style={{color:selected===s.id?palette.selectedText:colors.gray600}}>{s.pointAt?t('map.lastPoint',{time:new Date(s.pointAt).toLocaleString(lang)}):t('map.waiting')}</Text>
      </TouchableOpacity>)}
      </ProfileSection>}
    </ScrollView>
  </View>;
}
const styles=StyleSheet.create({screen:{flex:1},hint:{paddingHorizontal:spacing.md,paddingBottom:8},
  canvas:{flex:1,minHeight:160,marginHorizontal:spacing.md,borderRadius:radius.control,overflow:'hidden'},
  empty:{flex:1,minHeight:100,alignItems:'center',justifyContent:'center',padding:spacing.md},
  actions:{flexDirection:'row',flexWrap:'wrap',justifyContent:'space-between',paddingHorizontal:spacing.md},
  list:{flexGrow:0,maxHeight:'30%'},listContent:{paddingBottom:spacing.md},row:{padding:12,borderRadius:radius.control,minHeight:64},
  name:{fontSize:16,fontWeight:'600',marginBottom:4}});
